import type { AppRuntimeConfig } from "./runtime-config-types";

type RuntimeConfigKey = Exclude<keyof AppRuntimeConfig, "PUBLIC_IS_TEST_ENV">;

export type RuntimeConfigValidation = {
  isValid: boolean;
  isMissingConfig: boolean;
  missingKeys: RuntimeConfigKey[];
  placeholderKeys: RuntimeConfigKey[];
};

const REQUIRED_STRING_KEYS: RuntimeConfigKey[] = [
  "MSAL_PUBLIC_CLIENT_ID",
  "MSAL_PUBLIC_AUTH_SCOPE",
  "MSAL_PUBLIC_BACKEND_SCOPE",
  "PUBLIC_API_URL"
];

const PLACEHOLDER_MARKERS = [
  "REPLACE_WITH_",
  "example.invalid",
  "YOUR_",
  "TODO"
];

const isPlaceholderValue = (value: string) =>
  PLACEHOLDER_MARKERS.some((marker) => value.includes(marker));

export const peekRuntimeConfig = (): Readonly<AppRuntimeConfig> | undefined =>
  window.__APP_CONFIG__;

export const getRuntimeConfig = (): Readonly<AppRuntimeConfig> => {
  const cfg = peekRuntimeConfig();
  if (!cfg) {
    throw new Error(
      "Runtime config not loaded. Ensure /config.js is included before the app bundle."
    );
  }
  return cfg;
};

export const validateRuntimeConfig = (): RuntimeConfigValidation => {
  const cfg = peekRuntimeConfig();

  if (!cfg) {
    return {
      isValid: false,
      isMissingConfig: true,
      missingKeys: [...REQUIRED_STRING_KEYS],
      placeholderKeys: []
    };
  }

  const missingKeys = REQUIRED_STRING_KEYS.filter((key) => !cfg[key]?.trim());
  const placeholderKeys = REQUIRED_STRING_KEYS.filter((key) => {
    const value = cfg[key];
    return Boolean(value?.trim()) && isPlaceholderValue(value);
  });

  return {
    isValid: missingKeys.length === 0 && placeholderKeys.length === 0,
    isMissingConfig: false,
    missingKeys,
    placeholderKeys
  };
};

export const getRuntimeConfigValidationMessage = (
  validation: RuntimeConfigValidation
): string => {
  if (validation.isMissingConfig) {
    return "Runtime config is missing. Load /config.js before the app bundle runs.";
  }

  const issues: string[] = [];

  if (validation.missingKeys.length > 0) {
    issues.push(`missing values for ${validation.missingKeys.join(", ")}`);
  }

  if (validation.placeholderKeys.length > 0) {
    issues.push(
      `placeholder values still present for ${validation.placeholderKeys.join(", ")}`
    );
  }

  return `Compliance Hub is not configured for sign-in yet: ${issues.join(
    "; "
  )}. Update the deploy-time runtime config and reload the app.`;
};
